import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Download, Loader2, ChevronLeft, RefreshCw, FileText } from "lucide-react";
import axios from "axios";
import { fetchRestaurant } from "../lib/api";
import { RestaurantHeader } from "../components/RestaurantHeader";
import { Toaster } from "../components/ui/toaster";
import { toast } from "../hooks/use-toast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SocialPlanPage() {
  const navigate = useNavigate();
  const [restaurant, setRestaurant] = useState(null);
  const [lang, setLang] = useState(() => localStorage.getItem("lang") || "it");
  const [content, setContent] = useState("");
  const [updatedAt, setUpdatedAt] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { localStorage.setItem("lang", lang); }, [lang]);
  useEffect(() => { fetchRestaurant().then(setRestaurant).catch(() => {}); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/social/plan`);
      setContent(res.data.content);
      setUpdatedAt(res.data.updated_at);
    } catch (e) {
      toast({ title: "Errore", description: e?.message || "Piano non disponibile" });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const download = () => {
    const blob = new Blob([content], { type: "text/markdown;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "SOCIAL_PLAN_IlPozzoDeiGiganti.md";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(a.href);
    toast({ title: "Scaricato ✅", description: "Piano social salvato sul dispositivo" });
  };

  const printPage = () => {
    window.print();
  };

  const fmtDate = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      return d.toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" });
    } catch { return iso; }
  };

  return (
    <div className="min-h-screen bg-[#f7ebdf]">
      <div className="print:hidden">
        <RestaurantHeader restaurant={restaurant} lang={lang} setLang={setLang} compact stickyBar />
      </div>
      <main className="max-w-4xl mx-auto px-4 py-6" data-testid="social-plan-page">
        {/* Toolbar */}
        <div className="print:hidden flex items-center gap-2 mb-4">
          <button
            onClick={() => navigate("/social")}
            className="inline-flex items-center gap-1.5 rounded-full bg-white border border-[#e8dcc8] text-[#4a1c17] hover:bg-[#faf3ee] px-3 py-1.5 text-sm font-medium"
            data-testid="plan-back"
          >
            <ChevronLeft className="h-4 w-4" /> Social
          </button>
          <div className="ml-auto flex flex-wrap gap-2">
            <button
              onClick={load}
              disabled={loading}
              className="inline-flex items-center gap-1.5 rounded-full bg-white border border-[#e8dcc8] text-[#4a1c17] hover:bg-[#faf3ee] px-3 py-1.5 text-sm font-medium disabled:opacity-60"
              data-testid="plan-reload"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Ricarica
            </button>
            <button
              onClick={printPage}
              className="inline-flex items-center gap-1.5 rounded-full bg-white border border-[#e8dcc8] text-[#4a1c17] hover:bg-[#faf3ee] px-3 py-1.5 text-sm font-medium"
              data-testid="plan-print"
            >
              <FileText className="h-4 w-4" /> Stampa
            </button>
            <button
              onClick={download}
              className="inline-flex items-center gap-1.5 rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-[#f7ebdf] px-3 py-1.5 text-sm font-semibold"
              data-testid="plan-download"
            >
              <Download className="h-4 w-4" /> Scarica .md
            </button>
          </div>
        </div>

        {/* Header banner */}
        <div className="print:hidden mb-6 rounded-2xl bg-gradient-to-br from-[#4a1c17] to-[#2b1410] text-[#f7ebdf] p-5 sm:p-6">
          <div className="text-[10px] uppercase tracking-widest text-[#c48b31] font-semibold">Piano Social</div>
          <h1 className="mt-1 font-serif text-2xl sm:text-3xl font-bold">Il Pozzo dei Giganti</h1>
          {updatedAt && (
            <p className="mt-1 text-xs opacity-80">Aggiornato al {fmtDate(updatedAt)}</p>
          )}
          <p className="mt-3 text-sm opacity-90 max-w-2xl">
            Il piano editoriale, i 3 pilastri contenutistici, 60 spunti bilingua pronti da girare, hashtag bank
            e formule caption che funzionano. Usa i pulsanti in alto per stampare o scaricare il documento.
          </p>
        </div>

        {/* Content */}
        {loading ? (
          <div className="py-20 flex justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-[#4a1c17]" />
          </div>
        ) : (
          <article
            className="prose-plan bg-white rounded-2xl border border-[#e8dcc8] shadow-sm p-5 sm:p-8"
            data-testid="plan-content"
          >
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
          </article>
        )}
      </main>
      <Toaster />

      {/* Scoped typography for the markdown body */}
      <style>{`
        .prose-plan { color: #2b1410; line-height: 1.7; font-size: 15px; }
        .prose-plan h1 { font-family: "Inter", sans-serif; font-size: 2rem; font-weight: 800; color: #2b1410; margin: 1.5rem 0 1rem; line-height: 1.15; border-bottom: 2px solid #4a1c17; padding-bottom: .5rem; }
        .prose-plan h2 { font-family: "Inter", sans-serif; font-size: 1.5rem; font-weight: 700; color: #4a1c17; margin: 2rem 0 .75rem; line-height: 1.2; }
        .prose-plan h3 { font-family: "Inter", sans-serif; font-size: 1.15rem; font-weight: 700; color: #4a1c17; margin: 1.5rem 0 .5rem; }
        .prose-plan h4 { font-size: 1rem; font-weight: 700; color: #7c2d20; margin: 1.25rem 0 .5rem; text-transform: none; }
        .prose-plan p { margin: .5rem 0 1rem; }
        .prose-plan ul, .prose-plan ol { padding-left: 1.25rem; margin: .5rem 0 1rem; }
        .prose-plan li { margin: .25rem 0; }
        .prose-plan li > p { margin: .1rem 0; }
        .prose-plan strong { color: #4a1c17; font-weight: 700; }
        .prose-plan em { color: #7c5a4e; }
        .prose-plan code { background: #f7ebdf; color: #4a1c17; padding: .1rem .35rem; border-radius: .35rem; font-size: .85em; font-weight: 600; }
        .prose-plan pre { background: #2b1410; color: #f7ebdf; padding: 1rem; border-radius: .75rem; overflow-x: auto; font-size: .85em; margin: 1rem 0; }
        .prose-plan pre code { background: transparent; color: inherit; padding: 0; }
        .prose-plan blockquote { border-left: 3px solid #c48b31; padding-left: 1rem; color: #7c5a4e; margin: 1rem 0; font-style: italic; }
        .prose-plan a { color: #4a1c17; text-decoration: underline; }
        .prose-plan hr { border: 0; border-top: 1px solid #e8dcc8; margin: 2rem 0; }
        .prose-plan table { width: 100%; border-collapse: collapse; margin: 1rem 0; font-size: .9em; }
        .prose-plan th { background: #f7ebdf; color: #4a1c17; text-align: left; padding: .5rem .75rem; border: 1px solid #e8dcc8; font-weight: 700; }
        .prose-plan td { padding: .5rem .75rem; border: 1px solid #e8dcc8; vertical-align: top; }
        .prose-plan tr:nth-child(even) td { background: #fbf5ee; }
        @media print {
          body { background: white !important; }
          .prose-plan { border: 0; box-shadow: none; padding: 0; }
          .prose-plan h1, .prose-plan h2, .prose-plan h3 { page-break-after: avoid; }
        }
      `}</style>
    </div>
  );
}
